import { useState, useEffect, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AudioWaveform,
  ArrowLeft,
  Plus,
  Music2,
  MoreVertical,
  Trash2,
  ArrowUp,
  ArrowDown,
  Clock,
  Loader2,
  Maximize2,
  Share2,
  GripVertical,
  ChevronUp,
  ChevronDown,
  FileText,
  Calendar,
  MapPin,
  Headphones,
  Download
} from "lucide-react";
import { toast } from "sonner";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ALL_KEYS = ["C", "C#", "Db", "D", "D#", "Eb", "E", "F", "F#", "Gb", "G", "G#", "Ab", "A", "A#", "Bb", "B"];

// Transpose a key by semitones
const transposeKey = (key, semitones) => {
  const normalizedKeys = ["C", "C#", "D", "D#", "E", "F", "F#", "G", "G#", "A", "A#", "B"];
  const flatToSharp = { "Db": "C#", "Eb": "D#", "Gb": "F#", "Ab": "G#", "Bb": "A#" };
  
  let normalizedKey = flatToSharp[key] || key;
  let index = normalizedKeys.indexOf(normalizedKey);
  if (index === -1) index = 0;
  
  let newIndex = (index + semitones + 12) % 12;
  return normalizedKeys[newIndex];
};

const SetlistDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { getAuthHeaders } = useAuth();
  
  const [setlist, setSetlist] = useState(null);
  const [librarySongs, setLibrarySongs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddSongDialog, setShowAddSongDialog] = useState(false);
  const [expandedSong, setExpandedSong] = useState(null);

  const fetchSetlist = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/setlists/${id}`, {
        headers: getAuthHeaders()
      });
      setSetlist(response.data);
    } catch (error) {
      toast.error("Error al cargar el setlist");
      navigate("/setlists");
    } finally {
      setLoading(false);
    }
  }, [id, getAuthHeaders, navigate]);

  const fetchLibrarySongs = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/library/songs`, {
        headers: getAuthHeaders()
      });
      setLibrarySongs(response.data);
    } catch (error) {
      console.error("Error fetching library:", error);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchSetlist();
    fetchLibrarySongs();
  }, [fetchSetlist, fetchLibrarySongs]);

  const addSongToSetlist = async (song) => {
    try {
      await axios.post(`${API_URL}/setlists/${id}/songs`, {
        song_id: song.id,
        transposed_key: song.original_key
      }, {
        headers: getAuthHeaders()
      });
      toast.success(`"${song.title}" agregada`);
      fetchSetlist();
      setShowAddSongDialog(false);
    } catch (error) {
      toast.error("Error al agregar canción");
    }
  };

  const removeSongFromSetlist = async (songIndex) => {
    try {
      await axios.delete(`${API_URL}/setlists/${id}/songs/${songIndex}`, {
        headers: getAuthHeaders()
      });
      toast.success("Canción eliminada del setlist");
      fetchSetlist();
    } catch (error) {
      toast.error("Error al eliminar");
    }
  };

  const moveSong = async (songIndex, direction) => {
    const newIndex = direction === "up" ? songIndex - 1 : songIndex + 1;
    if (newIndex < 0 || newIndex >= setlist.songs.length) return;
    
    try {
      await axios.put(`${API_URL}/setlists/${id}/songs/${songIndex}/move`, {
        new_index: newIndex
      }, {
        headers: getAuthHeaders()
      });
      fetchSetlist();
    } catch (error) {
      toast.error("Error al mover canción");
    }
  };

  const updateSongKey = async (songIndex, newKey) => {
    try {
      await axios.put(`${API_URL}/setlists/${id}/songs/${songIndex}`, {
        transposed_key: newKey
      }, {
        headers: getAuthHeaders()
      });
      fetchSetlist();
    } catch (error) {
      toast.error("Error al cambiar tonalidad");
    }
  };

  const handleShare = () => {
    toast.info("Compartir publico aun no esta disponible. Exporta PDF para compartir la setlist por ahora.");
  };

  const handleDownloadPdf = async (compact = false) => {
    try {
      const response = await axios.get(`${API_URL}/setlists/${id}/export-pdf?compact=${compact}`, {
        headers: getAuthHeaders(),
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `setlist_${setlist?.name?.replace(/\s+/g, '_') || 'export'}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("PDF descargado");
    } catch (error) {
      toast.error("Error al generar PDF");
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    return date.toLocaleDateString("es-ES", { 
      weekday: "long",
      day: "numeric", 
      month: "long",
      year: "numeric"
    });
  };

  const calculateTotalDuration = () => {
    if (!setlist?.songs) return 0;
    return Math.round(setlist.songs.reduce((acc, s) => acc + (s.duration_minutes || 4), 0));
  };

  // Songs not yet in setlist
  const availableSongs = librarySongs.filter(
    ls => !setlist?.songs?.some(ss => ss.song_id === ls.id)
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-[#09090b] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-primary animate-spin" />
      </div>
    );
  }

  if (!setlist) {
    return (
      <div className="min-h-screen bg-[#09090b] flex items-center justify-center">
        <p className="text-gray-400">Setlist no encontrado</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#09090b]">
      {/* Header */}
      <header className="sticky top-0 z-50 glass-panel">
        <div className="max-w-5xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/setlists" className="text-gray-400 hover:text-white transition-colors">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div>
              <h1 className="font-semibold text-white font-['Manrope']">{setlist.name}</h1>
              {setlist.venue && (
                <p className="text-xs text-gray-500">{setlist.venue}</p>
              )}
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="border-white/10 text-gray-300 gap-2">
                  <Download className="w-4 h-4" />
                  <span className="hidden md:inline">PDF</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-[#18181b] border-white/10">
                <DropdownMenuItem onClick={() => handleDownloadPdf(false)} className="text-gray-300">
                  PDF Normal (escenario)
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleDownloadPdf(true)} className="text-gray-300">
                  PDF Compacto (ahorra papel)
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Button variant="outline" size="sm" onClick={handleShare} className="border-white/10 text-gray-300 gap-2">
              <Share2 className="w-4 h-4" />
              <span className="hidden md:inline">Compartir</span>
            </Button>
            <Link to={`/rehearsal/${id}`}>
              <Button variant="outline" size="sm" className="border-accent/50 text-accent hover:bg-accent/10 gap-2" data-testid="rehearsal-mode-btn">
                <Headphones className="w-4 h-4" />
                <span className="hidden md:inline">Ensayar</span>
              </Button>
            </Link>
            <Link to={`/stage/${id}`}>
              <Button className="bg-secondary hover:bg-secondary/90 gap-2" data-testid="stage-mode-btn">
                <Maximize2 className="w-4 h-4" />
                <span className="hidden md:inline">Escenario</span>
              </Button>
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-8">
        {/* Setlist Info */}
        <Card className="bg-[#18181b] border-white/10 mb-6">
          <CardContent className="p-6">
            <div className="flex flex-wrap items-center gap-4">
              {setlist.event_date && (
                <div className="flex items-center gap-2 text-gray-400">
                  <Calendar className="w-4 h-4" />
                  <span>{formatDate(setlist.event_date)}</span>
                </div>
              )}
              {setlist.venue && (
                <div className="flex items-center gap-2 text-gray-400">
                  <MapPin className="w-4 h-4" />
                  <span>{setlist.venue}</span>
                </div>
              )}
              <div className="flex items-center gap-2 text-gray-400">
                <Music2 className="w-4 h-4" />
                <span>{setlist.songs?.length || 0} canciones</span>
              </div>
              <div className="flex items-center gap-2 text-gray-400">
                <Clock className="w-4 h-4" />
                <span>{calculateTotalDuration()} minutos</span>
              </div>
            </div>
            {setlist.notes && (
              <p className="text-gray-500 mt-3 text-sm">{setlist.notes}</p>
            )}
          </CardContent>
        </Card>

        {/* Songs List */}
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-white font-['Manrope']">Canciones</h2>
          <Button onClick={() => setShowAddSongDialog(true)} className="bg-primary hover:bg-primary/90 gap-2" data-testid="add-song-to-setlist">
            <Plus className="w-4 h-4" />
            Agregar
          </Button>
        </div>

        {(!setlist.songs || setlist.songs.length === 0) ? (
          <Card className="bg-[#18181b] border-white/10 border-dashed">
            <CardContent className="py-12 text-center">
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mx-auto mb-3">
                <Music2 className="w-6 h-6 text-primary" />
              </div>
              <p className="text-gray-400 mb-4">Este setlist está vacío</p>
              <Button onClick={() => setShowAddSongDialog(true)} variant="outline" className="border-white/10 text-gray-300 gap-2">
                <Plus className="w-4 h-4" />
                Agregar canciones
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-2">
            {setlist.songs.map((song, index) => (
              <Card 
                key={index} 
                className={`bg-[#18181b] border-white/10 transition-all ${expandedSong === index ? "ring-1 ring-primary/50" : ""}`}
                data-testid={`setlist-song-${index}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    {/* Drag handle & number */}
                    <div className="flex items-center gap-2 text-gray-600">
                      <GripVertical className="w-4 h-4 cursor-move" />
                      <span className="w-6 text-center font-mono">{index + 1}</span>
                    </div>
                    
                    {/* Song info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="font-medium text-white truncate">{song.title}</h3>
                        {song.notes && (
                          <FileText className="w-3 h-3 text-accent flex-shrink-0" />
                        )}
                      </div>
                      <p className="text-sm text-gray-500 truncate">{song.artist || "Sin artista"}</p>
                    </div>
                    
                    {/* Key selector */}
                    <Select 
                      value={song.transposed_key || song.original_key} 
                      onValueChange={(v) => updateSongKey(index, v)}
                    >
                      <SelectTrigger className="w-20 bg-primary/20 border-primary/30 text-primary font-mono">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#18181b] border-white/10">
                        {ALL_KEYS.map(key => (
                          <SelectItem key={key} value={key} className="text-white font-mono">
                            {key} {key === song.original_key ? "(orig)" : ""}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    
                    {/* BPM */}
                    {song.bpm && (
                      <span className="text-sm font-mono text-gray-500 hidden md:block w-16 text-right">
                        {song.bpm} BPM
                      </span>
                    )}
                    
                    {/* Duration */}
                    <span className="text-sm text-gray-500 hidden md:block w-12 text-right">
                      {song.duration_minutes || 4}m
                    </span>
                    
                    {/* Actions */}
                    <div className="flex items-center gap-1">
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        onClick={() => moveSong(index, "up")}
                        disabled={index === 0}
                        className="text-gray-500 hover:text-white h-8 w-8"
                      >
                        <ChevronUp className="w-4 h-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        onClick={() => moveSong(index, "down")}
                        disabled={index === setlist.songs.length - 1}
                        className="text-gray-500 hover:text-white h-8 w-8"
                      >
                        <ChevronDown className="w-4 h-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        onClick={() => setExpandedSong(expandedSong === index ? null : index)}
                        className="text-gray-500 hover:text-white h-8 w-8"
                      >
                        <FileText className="w-4 h-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        onClick={() => removeSongFromSetlist(index)}
                        className="text-gray-500 hover:text-red-400 h-8 w-8"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  
                  {/* Expanded notes */}
                  {expandedSong === index && song.notes && (
                    <div className="mt-4 pt-4 border-t border-white/5 pl-12">
                      <p className="text-sm text-gray-400 whitespace-pre-wrap">{song.notes}</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Add Song Dialog */}
        <Dialog open={showAddSongDialog} onOpenChange={setShowAddSongDialog}>
          <DialogContent className="bg-[#18181b] border-white/10 max-w-2xl max-h-[80vh]">
            <DialogHeader>
              <DialogTitle className="text-white font-['Manrope']">Agregar Canción</DialogTitle>
            </DialogHeader>
            
            {availableSongs.length === 0 ? (
              <div className="py-8 text-center">
                <p className="text-gray-400 mb-4">
                  {librarySongs.length === 0 
                    ? "Tu biblioteca está vacía" 
                    : "Todas las canciones ya están en el setlist"}
                </p>
                <Link to="/library">
                  <Button variant="outline" className="border-white/10 text-gray-300 gap-2">
                    <Plus className="w-4 h-4" />
                    Ir a Biblioteca
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="space-y-2 max-h-[50vh] overflow-y-auto">
                {availableSongs.map((song) => (
                  <div 
                    key={song.id}
                    onClick={() => addSongToSetlist(song)}
                    className="flex items-center gap-4 p-3 rounded-lg bg-black/30 hover:bg-white/5 cursor-pointer transition-colors"
                  >
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                      <Music2 className="w-5 h-5 text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-medium text-white truncate">{song.title}</h4>
                      <p className="text-sm text-gray-500 truncate">{song.artist}</p>
                    </div>
                    <Badge className="bg-primary/20 text-primary border-0 font-mono">
                      {song.original_key}
                    </Badge>
                    <Plus className="w-4 h-4 text-gray-400" />
                  </div>
                ))}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
};

export default SetlistDetailPage;
